import {useRef} from 'react'
import {useApolloClient, useMutation} from '@apollo/client/react'

import {CREATE_DOWNLOAD_PRESIGNER, EXTEND_DOWNLOAD} from '../../graphql/mutations/downloads'
import {DOWNLOAD_PRESIGNER} from '../../graphql/queries/downloads'
import {STARRED_UPLOADS, UPLOAD_DETAILS, UPLOADS} from '../../graphql/queries/uploads'
import {parseTimestamp} from '../fileTools'

// Opening a file is a job that decrypts the whole of it, so it is asked after often, and for a
// while, before it is given up on. Big files take minutes to decrypt, so the wait grows with the
// file: two minutes, and a second more for every megabyte.
const POLL_INTERVAL = 1000
const BASE_POLL_ATTEMPTS = 120
const BYTES_PER_EXTRA_ATTEMPT = 1024 * 1024

const pollAttempts = file => BASE_POLL_ATTEMPTS + Math.ceil((file.filesize ?? 0) / BYTES_PER_EXTRA_ATTEMPT)

// A url runs out when its copy is taken away, so it is handed out again only while there is a
// minute left to fetch it in.
const REUSE_MARGIN = 60 * 1000

// A copy being played from is kept on once it has less than this left, well before the player
// could reach its end.
const KEEP_WHEN_LEFT = 5 * 60 * 1000

const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))

/**
 * Fetches a prepared copy of a file into memory, typed as storage serves it. Needs storage to allow
 * this origin to GET, which it already must to PUT uploads.
 */
export const readFile = async (url, file) => {
  const response = await fetch(url)

  if (!response.ok) throw new Error(`Storage refused ${file.filename} (${response.status})`)

  return response.blob()
}

// The copy is stored as an attachment named for the file, so following its url saves it rather
// than leaving the drive, and the browser streams it to disk however large it is.
const follow = url => {
  const anchor = Object.assign(document.createElement('a'), {href: url})

  document.body.append(anchor)
  anchor.click()
  anchor.remove()
}

/**
 * Gets readable copies of secured files, for the preview to show and for saving to disk.
 *
 * Every file in the drive is stored encrypted. Asking to download one queues a job that decrypts
 * it into a copy of its own, which the API presigns once it is ready — so "locate" asks, then asks
 * after it every second until there is a url to hand back, and gives up once the file has had
 * longer than its size should need. A url is held and handed out again while its copy is sure to
 * still be there, so opening a file and then downloading it, or stepping back to it, does not
 * decrypt it twice. What is held is the request itself, so two asks for the same file at once
 * share one copy too. The API hands back a copy opened for someone else's ask as well, while
 * there is one, and then there is no wait at all.
 *
 * A copy is taken away when it expires, which would cut off a video still being watched, so a
 * player calls "keep" while it plays. Near the copy's expiry that asks the API to keep it on, and
 * holds the url that now runs as long as it does. When the player loses its copy anyway, "relocate"
 * lets go of the url it lost and finds another, from a copy kept on or a new one.
 */
const useDownloads = () => {
  const client = useApolloClient()
  const [createPresigner] = useMutation(CREATE_DOWNLOAD_PRESIGNER)
  const [extendDownload] = useMutation(EXTEND_DOWNLOAD)
  const held = useRef(new Map())

  const preparedDownload = async ({uploadId, id}, attempts) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      await wait(POLL_INTERVAL)

      const {data} = await client.query({query: DOWNLOAD_PRESIGNER, variables: {uploadId, id}, fetchPolicy: 'network-only'})

      if (data.downloadPresigner.presignedUrl) return data.downloadPresigner
    }

    throw new Error(`Gave up waiting for download ${id} to be prepared`)
  }

  // Preparing a copy gives a file secured before thumbnails and details were made its thumbnail and
  // its details, so the listings, and the details open beside it, are asked for again to show them.
  // Recent is left as it is, since asking it again would move the file just opened to its top.
  const refreshThumbnails = download => {
    client.refetchQueries({include: [UPLOADS, STARRED_UPLOADS, UPLOAD_DETAILS]})

    return download
  }

  const prepare = file => createPresigner({variables: {id: file.id}})
    .then(({data: {createDownloadPresigner: presigner}}) => (
      presigner.presignedUrl ? presigner : preparedDownload(presigner, pollAttempts(file)).then(refreshThumbnails)
    ))

  // A holding without an expiry is still being prepared, and is shared until it is.
  const reusable = holding => Boolean(holding) &&
    (holding.expiresAt === null || holding.expiresAt - Date.now() > REUSE_MARGIN)

  const locate = file => {
    const holding = held.current.get(file.id)

    if (reusable(holding)) return holding.url

    const download = prepare(file)
    const fresh = {url: download.then(({presignedUrl}) => presignedUrl), expiresAt: null}

    held.current.set(file.id, fresh)
    download.then(
      ({uploadId, id, expiresAt}) => {
        fresh.download = {uploadId, id}
        fresh.expiresAt = parseTimestamp(expiresAt).getTime()
      },
      // Only a copy that came is worth holding on to; a failure is asked about afresh next time.
      () => held.current.delete(file.id)
    )

    return fresh.url
  }

  const letGo = (file, holding) => {
    if (held.current.get(file.id) === holding) held.current.delete(file.id)
  }

  const keep = async file => {
    const holding = held.current.get(file.id)

    if (!holding?.download || holding.expiresAt - Date.now() > KEEP_WHEN_LEFT) return

    const {data: {extendDownload: kept}} = await extendDownload({variables: holding.download})

    // Gone already, so the next ask prepares another.
    if (!kept.presignedUrl) {
      letGo(file, holding)
      return
    }

    holding.url = Promise.resolve(kept.presignedUrl)
    holding.expiresAt = parseTimestamp(kept.expiresAt).getTime()
  }

  const relocate = async (file, lost) => {
    const holding = held.current.get(file.id)
    const url = await holding?.url.catch(() => null)

    if (url === lost) letGo(file, holding)

    return locate(file)
  }

  const save = file => locate(file).then(follow)

  return {locate, keep, relocate, save}
}

export default useDownloads
